/**
 * OpsLayout — shared shell for every ops page: side nav + top bar.
 * Menu items are role-aware.
 */
import { Link, useLocation } from 'react-router-dom';
import { useOpsAuth } from './OpsAuthContext';
import NotificationBell from './NotificationBell';
import AccountMenu from './AccountMenu';
import brandLogo from '../assets/childhood-horizons-logo.jpeg';

const ROLES_ALL = ['executive_director', 'operations_manager', 'track_leader', 'member'];

const NAV = [
  { to: '/ops', label: 'الرئيسية', roles: ROLES_ALL },
  { to: '/ops/inbox', label: 'صندوق الطلبات', roles: ['executive_director', 'operations_manager'] },
  { to: '/ops/newsletter', label: 'النشرة البريدية', roles: ['executive_director', 'operations_manager'] },
  { to: '/ops/projects', label: 'المشاريع', roles: ['executive_director', 'operations_manager', 'track_leader'] },
  { to: '/ops/approvals', label: 'الاعتمادات', roles: ['executive_director'] },
  { to: '/ops/knowledge', label: 'مركز المعرفة', roles: ['executive_director'] },
  { to: '/ops/my-tasks', label: 'مهامي', roles: ['executive_director', 'track_leader', 'member'] },
  { to: '/ops/team', label: 'فريق العمل', roles: ['executive_director'] },
  { to: '/ops/analytics/team', label: 'مؤشرات الفريق', roles: ['executive_director', 'operations_manager'] },
  { to: '/ops/settings', label: 'الإعدادات', roles: ['executive_director'] },
];

const OpsLayout = ({ title, subtitle, children }) => {
  const { user } = useOpsAuth();
  const { pathname } = useLocation();

  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div dir="rtl" className="min-h-screen bg-[#F7F5F2] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-white border-l border-[#E6E0D6] shrink-0">
        <div className="px-5 py-6 border-b border-[#EFEAE1]">
          <img
            src={brandLogo}
            alt="Childhood Horizons"
            className="w-16 h-16 object-contain"
            style={{ mixBlendMode: 'multiply' }}
          />
          <p className="mt-2 font-['Playfair_Display'] italic text-[12px] text-[#7A866E]">
            Internal Ops
          </p>
        </div>
        <nav className="flex-1 px-3 py-4">
          {items.map((it) => {
            const active = pathname === it.to || (it.to !== '/ops' && pathname.startsWith(it.to));
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.to === '/ops' ? 'home' : it.to.split('/').pop()}`}
                className={`block px-3 py-2.5 rounded-lg text-[13px] font-['Noto_Kufi_Arabic'] mb-1 transition ${
                  active
                    ? 'bg-[#1F3D2B] text-white'
                    : 'text-[#3D512D] hover:bg-[#F3F1E9]'
                }`}
              >
                {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-[#EFEAE1] text-[11px] font-['Noto_Kufi_Arabic'] text-[#7A866E]">
          © Childhood Horizons
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#E6E0D6] px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-['Noto_Kufi_Arabic'] text-[20px] font-bold text-[#1F3D2B] leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="font-['Playfair_Display'] italic text-[12px] text-[#7A866E] mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <AccountMenu />
          </div>
        </header>

        <div className="p-6">
          {/* Mobile nav bar (visible only < md) */}
          <div className="md:hidden mb-4 flex gap-2 overflow-x-auto">
            {items.map((it) => {
              const active = pathname === it.to || (it.to !== '/ops' && pathname.startsWith(it.to));
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  className={`shrink-0 text-[12px] font-['Noto_Kufi_Arabic'] px-3 py-1.5 rounded-lg ${
                    active ? 'bg-[#1F3D2B] text-white' : 'bg-white border border-[#E6E0D6] text-[#3D512D]'
                  }`}
                >
                  {it.label}
                </Link>
              );
            })}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default OpsLayout;
